import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { statsService, StatsOverview } from '../api/statsService';
import { streakService, UserStreak } from '../api/streakService';
import { useAuth } from './AuthContext';

interface StatsRefreshContextType {
    statsVersion: number;
    refreshStats: () => void;
    /** GET /api/v1/stats/overview, fetched once here and shared — Navigation, the sidebar
     * Progress/Challenge widgets, and RecentActivity used to each independently re-fetch the
     * exact same overview data on every page load. null until the first fetch resolves (or if
     * it fails, or before the learning language is known). */
    overview: StatsOverview | null;
    overviewLoading: boolean;
    /** GET /api/v1/streaks/me, fetched once here and shared (Navigation's today XP, the
     * Challenge widget's streak freezes) — same consolidation as `overview`. null until loaded
     * or if the fetch fails. */
    streak: UserStreak | null;
    /** Replace the shared streak with a fresher copy (e.g. the response of buying a freeze). */
    setStreak: (streak: UserStreak) => void;
}

const StatsRefreshContext = createContext<StatsRefreshContextType>({
    statsVersion: 0,
    refreshStats: () => {},
    overview: null,
    overviewLoading: true,
    streak: null,
    setStreak: () => {},
});

export const StatsRefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [statsVersion, setStatsVersion] = useState(0);
    const refreshStats = useCallback(() => setStatsVersion(v => v + 1), []);

    const auth = useAuth();
    const learningLanguage = auth?.userSettings?.learning_language;
    const [overview, setOverview] = useState<StatsOverview | null>(null);
    const [overviewLoading, setOverviewLoading] = useState(true);
    const [streak, setStreak] = useState<UserStreak | null>(null);

    useEffect(() => {
        if (!learningLanguage) {
            // Nothing to fetch — without this, overviewLoading (initialized true) would stay
            // true forever, permanently stalling every widget that gates on it.
            setOverview(null);
            setOverviewLoading(false);
            return;
        }
        let cancelled = false;
        setOverviewLoading(true);
        statsService.getOverview(learningLanguage)
            .then(data => { if (!cancelled) setOverview(data); })
            .catch(() => { if (!cancelled) setOverview(null); })
            .finally(() => { if (!cancelled) setOverviewLoading(false); });
        streakService.getMyStreak()
            .then(data => { if (!cancelled) setStreak(data); })
            .catch(() => { if (!cancelled) setStreak(null); });
        return () => { cancelled = true; };
    }, [learningLanguage, statsVersion]);

    return (
        <StatsRefreshContext.Provider value={{ statsVersion, refreshStats, overview, overviewLoading, streak, setStreak }}>
            {children}
        </StatsRefreshContext.Provider>
    );
};

export const useStatsRefresh = () => useContext(StatsRefreshContext);
